import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

import Loading from "components/common/Loading";
import {
  getEmployeeById,
  updateEmployee,
  getOfficeByTenant,
  getShiftsByOffice,
  getEmployeeCurrentShift,
  updateEmployeeShift,
} from "services/hrms.api";

export default function EditEmployee() {
  const { id } = useParams(); // this is the db _id (exposed as id)
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [emp, setEmp] = useState(null);

  // shift UI
  const [shiftSaving, setShiftSaving] = useState(false);
  const [shiftError, setShiftError] = useState("");
  const [offices, setOffices] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [currentAssign, setCurrentAssign] = useState(null);

  const [shiftForm, setShiftForm] = useState({
    officeId: "",
    shiftId: "",
    effectiveFrom: "", // YYYY-MM-DD
  });

  useEffect(() => {
    (async () => {
      try {
        setError("");
        setLoading(true);
        const { data } = await getEmployeeById(id);
        setEmp(data);
      } catch (e) {
        console.error(e);
        setError("Failed to load employee.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);


  const User = localStorage.getItem("user");
  const tenantUser = JSON.parse(User || "{}");
  const tenantId = tenantUser?.tenant;

  useEffect(() => {
    (async () => {
      try {
        setError("");
        setLoading(true);

        const [empRes, officeRes, currentShiftRes] = await Promise.allSettled([
          getEmployeeById(id),
          getOfficeByTenant(tenantId),
          getEmployeeCurrentShift(id),
        ]);

        if (empRes.status === "fulfilled") setEmp(empRes.value.data);
        else throw new Error("Failed to load employee");

        if (officeRes.status === "fulfilled") {
          const items = officeRes.value.data?.items || [];
          setOffices(items);
        }

        if (currentShiftRes.status === "fulfilled") {
          const cs = currentShiftRes.value.data || null;
          setCurrentAssign(cs);

          const officeId = cs?.office?._id || cs?.office || "";
          const shiftId = cs?.shift?._id || cs?.shift || "";

          // default effectiveFrom = today (or keep blank)
          const today = new Date().toISOString().slice(0, 10);

          setShiftForm({
            officeId,
            shiftId,
            effectiveFrom: today,
          });

          // load shifts list for that office
          if (officeId) {
            const s = await getShiftsByOffice(officeId);
            const list = s?.data || [];
            setShifts(Array.isArray(list) ? list : list?.items || []);
          }
        }
      } catch (e) {
        console.error(e);
        setError("Failed to load employee.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, tenantId]);

  const loadShifts = async (officeId) => {
    setShiftError("");
    setShifts([]);
    if (!officeId) return;
    try {
      const res = await getShiftsByOffice(officeId);
      const list = res?.data || [];
      setShifts(Array.isArray(list) ? list : list?.items || []);
    } catch (e) {
      setShiftError(e?.message || "Failed to load shifts");
    }
  };

  if (loading) {
    return <Loading variant="dots" fullscreen />;
  }

  if (error || !emp) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50 dark:border-white/10 dark:hover:bg-white/5"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>
        <div className="border-rose-200 bg-rose-50 text-rose-700 dark:text-rose-300 mt-4 rounded-lg border p-3 text-sm dark:border-white/10 dark:bg-white/5">
          {error || "Employee not found."}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto space-y-6 p-4 md:p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
          <h2 className="text-2xl font-semibold dark:text-white">
            Edit Employee
          </h2>
        </div>
      </div>

      {/* ✅ SHIFT ASSIGNMENT PANEL */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800">
        <h3 className="mb-3 text-lg font-semibold text-gray-800 dark:text-white">
          Shift Assignment
        </h3>

        {shiftError ? (
          <div className="mb-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-700 dark:bg-white/5 dark:text-rose-300">
            {shiftError}
          </div>
        ) : null}

        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Office">
            <select
              value={shiftForm.officeId}
              onChange={async (e) => {
                const officeId = e.target.value;
                setShiftForm((p) => ({
                  ...p,
                  officeId,
                  shiftId: "",
                }));
                await loadShifts(officeId);
              }}
              className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
            >
              <option value="">Select Office</option>
              {offices.map((o) => (
                <option key={o._id || o.id} value={o._id || o.id}>
                  {o.name || "Office"} {o.city ? `- ${o.city}` : ""}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Shift">
            <select
              value={shiftForm.shiftId}
              onChange={(e) =>
                setShiftForm((p) => ({ ...p, shiftId: e.target.value }))
              }
              className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
              disabled={!shiftForm.officeId}
            >
              <option value="">Select Shift</option>
              {shifts.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name} ({s.startTime} - {s.endTime})
                </option>
              ))}
            </select>
          </Field>

          <Field label="Effective From">
            <input
              type="date"
              value={shiftForm.effectiveFrom}
              onChange={(e) =>
                setShiftForm((p) => ({ ...p, effectiveFrom: e.target.value }))
              }
              className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
            />
          </Field>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="text-xs text-gray-500 dark:text-white/60">
            Current:{" "}
            <span className="font-medium">
              {currentAssign?.shift?.name
                ? `${currentAssign.shift.name} (${currentAssign.shift.startTime}-${currentAssign.shift.endTime})`
                : "-"}
            </span>{" "}
            •{" "}
            <span className="font-medium">
              {currentAssign?.office?.name || "-"}
            </span>
          </div>

          <button
            type="button"
            disabled={
              shiftSaving ||
              !shiftForm.officeId ||
              !shiftForm.shiftId ||
              !shiftForm.effectiveFrom
            }
            onClick={async () => {
              setShiftError("");
              try {
                setShiftSaving(true);

                await updateEmployeeShift(id, {
                  officeId: shiftForm.officeId,
                  shiftId: shiftForm.shiftId,
                  effectiveFrom: shiftForm.effectiveFrom, // YYYY-MM-DD
                });

                // refresh current assignment
                const fresh = await getEmployeeCurrentShift(id);
                setCurrentAssign(fresh.data || null);
              } catch (e) {
                setShiftError(e?.message || "Failed to update shift");
              } finally {
                setShiftSaving(false);
              }
            }}
            className="rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-5 py-2 text-sm text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {shiftSaving ? "Updating..." : "Update Shift"}
          </button>
        </div>
      </div>

      {/* ✅ PROFILE EDIT PANEL (YOUR EXISTING FORM) */}
      <div className="rounded-2xl border border-gray-200 p-4 dark:border-white/10">
        <EmployeeForm
          initialData={emp}
          saving={saving}
          onCancel={() => navigate(-1)}
          onSubmit={async (payload) => {
            try {
              setSaving(true);
              // if your API expects /employees/:id with the db id
              await updateEmployee(emp.id || emp._id, payload);
              navigate(-1);
            } catch (e) {
              console.error(e);
              alert(e?.message || "Failed to update employee");
            } finally {
              setSaving(false);
            }
          }}
        />
      </div>
    </div>
  );
}

function EmployeeForm({ initialData, onSubmit, onCancel, saving }) {
  const departmentOptions = [
    "Finance",
    "Human Resources",
    "Sales",
    "IT",
    "Operations",
    "Other",
  ];

  const designationOptions = [
    "Accountant",
    "Software Developer",
    "Manager",
    "HR Executive",
    "Executive Officer",
    "Other",
  ];

  // Normalize initial data from backend to form fields
  const [form, setForm] = useState(() => {
    const deptIsPredefined = departmentOptions.includes(
      initialData?.department
    );
    const desgIsPredefined = designationOptions.includes(
      initialData?.designation
    );

    const status = (initialData?.status || "active").toLowerCase();

    return {
      code: initialData?.code || "",

      firstName: initialData?.firstName || "",
      lastName: initialData?.lastName || "",
      email: initialData?.email || "",
      phone: initialData?.phone || "",
      company: initialData?.company || "",

      // ✅ NEW: username (editable) + optional password change
      username: initialData?.username || "",
      password: "",
      confirmPassword: "",

      panNumber: initialData?.panNumber || "",
      aadhaarNumber: initialData?.aadhaarNumber || "",

      // department
      departmentSelect: deptIsPredefined
        ? initialData?.department || ""
        : initialData?.department
          ? "Other"
          : "",
      department: deptIsPredefined ? "" : initialData?.department || "",

      // designation
      designationSelect: desgIsPredefined
        ? initialData?.designation || ""
        : initialData?.designation
          ? "Other"
          : "",
      designation: desgIsPredefined ? "" : initialData?.designation || "",

      dob: initialData?.dob
        ? new Date(initialData.dob).toISOString().slice(0, 10)
        : "",
      gender: initialData?.gender || "",
      address: initialData?.address || "",

      joinDate: initialData?.joinDate
        ? new Date(initialData.joinDate).toISOString().slice(0, 10)
        : "",

      // ✅ checkbox controls status
      active: status !== "inactive",

      monthlySalary:
        typeof initialData?.monthlySalary === "number"
          ? String(initialData.monthlySalary)
          : initialData?.monthlySalary
            ? String(initialData.monthlySalary)
            : "",
    };
  });

  const set = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const canSave = useMemo(() => {
    return form.firstName.trim() && form.lastName.trim();
  }, [form.firstName, form.lastName]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSave) return;

    // simple salary validation (optional but useful)
    if (form.monthlySalary) {
      const n = Number(form.monthlySalary);
      if (Number.isNaN(n) || n < 0) {
        alert("Monthly salary must be a valid non-negative number.");
        return;
      }
    }

    // ✅ Password is optional on edit — only validate if the person is
    // actually trying to change it.
    if (form.password || form.confirmPassword) {
      if (form.password.length < 6) {
        alert("Password must be at least 6 characters.");
        return;
      }
      if (form.password !== form.confirmPassword) {
        alert("Passwords do not match.");
        return;
      }
    }

    // Resolve final department & designation values correctly
    const finalDepartment =
      form.departmentSelect === "Other"
        ? form.department.trim() || undefined
        : form.departmentSelect || undefined;

    const finalDesignation =
      form.designationSelect === "Other"
        ? form.designation.trim() || undefined
        : form.designationSelect || undefined;

    // Build payload the backend expects
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),

      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      company: form.company.trim() || undefined,

      // ✅ NEW: username always sent (kept in sync); password only if changed
      username: form.username.trim()
        ? form.username.trim().toLowerCase()
        : undefined,

      department: finalDepartment,
      designation: finalDesignation,

      joinDate: form.joinDate || null,
      dob: form.dob || null,

      gender: form.gender || undefined,
      address: form.address?.trim() || undefined,

      panNumber: form.panNumber.trim()
        ? form.panNumber.trim().toUpperCase()
        : undefined,

      aadhaarNumber: form.aadhaarNumber.trim()
        ? form.aadhaarNumber.replace(/\s+/g, "")
        : undefined,

      // ⭐ NEW: send salary if entered
      monthlySalary:
        form.monthlySalary !== "" ? Number(form.monthlySalary) : undefined,

      // ✅ IMPORTANT: status not active
      status: form.active ? "active" : "inactive",
    };

    // ✅ Only include password if the person typed one — backend leaves the
    // existing password untouched when this key is absent.
    if (form.password) {
      payload.password = form.password;
    }

    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
      <Field label="Employee ID">
        <input
          name="code"
          value={form.code}
          readOnly
          className="w-full cursor-not-allowed rounded-lg border px-3 py-2 text-sm opacity-80 dark:border-white/10 dark:bg-navy-800 dark:bg-white/5 dark:text-white"
        />
      </Field>

      <Field label="Username">
        <input
          name="username"
          value={form.username}
          onChange={set}
          autoComplete="off"
          className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
        />
      </Field>

      <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-2">
        <Field label="First Name">
          <input
            name="firstName"
            value={form.firstName}
            onChange={set}
            required
            className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
          />
        </Field>
        <Field label="Last Name">
          <input
            name="lastName"
            value={form.lastName}
            onChange={set}
            required
            className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
          />
        </Field>
      </div>

      {/* ✅ Optional password change */}
      <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-2">
        <Field label="New Password (leave blank to keep current)">
          <input
            type="password"
            name="password"
            value={form.password}
            onChange={set}
            autoComplete="new-password"
            className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
          />
        </Field>
        <Field label="Confirm New Password">
          <input
            type="password"
            name="confirmPassword"
            value={form.confirmPassword}
            onChange={set}
            autoComplete="new-password"
            className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
          />
        </Field>
      </div>

      <Field label="PAN Number">
        <input
          name="panNumber"
          value={form.panNumber}
          onChange={set}
          placeholder="ABCDE1234F"
          className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
        />
      </Field>

      <Field label="Aadhaar Number">
        <input
          name="aadhaarNumber"
          value={form.aadhaarNumber}
          onChange={set}
          placeholder="12-digit Aadhaar"
          className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
        />
      </Field>

      <Field label="Email">
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={set}
          className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
        />
      </Field>

      <Field label="Phone">
        <input
          name="phone"
          value={form.phone}
          onChange={set}
          className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
        />
      </Field>

      <Field label="Date of Birth (DOB)">
        <input
          type="date"
          name="dob"
          value={form.dob}
          onChange={set}
          className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
        />
      </Field>

      <Field label="Gender">
        <select
          name="gender"
          value={form.gender}
          onChange={set}
          className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
        >
          <option value="">Select Gender</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other</option>
        </select>
      </Field>

      <Field label="Company">
        <input
          name="company"
          value={form.company}
          onChange={set}
          className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
        />
      </Field>

      {/* ⭐ Monthly Salary */}
      <Field label="Monthly Salary (30 days)">
        <input
          type="number"
          name="monthlySalary"
          value={form.monthlySalary}
          onChange={set}
          className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
          placeholder="e.g. 30000"
        />
      </Field>

      <Field label="Department">
        <select
          name="departmentSelect"
          value={form.departmentSelect}
          onChange={set}
          className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
        >
          <option value="">Select</option>
          {departmentOptions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>

        {form.departmentSelect === "Other" && (
          <input
            type="text"
            name="department"
            placeholder="Enter Department"
            value={form.department}
            onChange={set}
            className="dark:bg-transparent mt-2 w-full rounded-lg border px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
          />
        )}
      </Field>

      <Field label="Designation">
        <select
          name="designationSelect"
          value={form.designationSelect}
          onChange={set}
          className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
        >
          <option value="">Select</option>
          {designationOptions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>

        {form.designationSelect === "Other" && (
          <input
            type="text"
            name="designation"
            placeholder="Enter Designation"
            value={form.designation}
            onChange={set}
            className="dark:bg-transparent mt-2 w-full rounded-lg border px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
          />
        )}
      </Field>

      <Field label="Joining Date">
        <input
          type="date"
          name="joinDate"
          value={form.joinDate || ""}
          onChange={set}
          className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white dark:border-white/10 dark:bg-navy-800 dark:text-white"
        />
      </Field>

      <Field label="Address">
        <textarea
          name="address"
          value={form.address}
          onChange={set}
          rows={2}
          className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
        />
      </Field>

      <div className="flex items-center justify-between rounded-lg border p-3 dark:border-white/10 md:col-span-2">
        <label className="flex items-center gap-2 text-sm dark:text-white">
          <input
            type="checkbox"
            name="active"
            checked={form.active}
            onChange={set}
            className="h-4 w-4"
          />
          Active
        </label>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave || saving}
            className="rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-5 py-2 text-sm text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-white/80">
        {label}
      </label>
      {children}
    </div>
  );
}
